import { useForm } from '@inertiajs/react'
import type { FieldErrors } from 'react-hook-form'

import { Button, Link, TextInput } from '@atoms'
import { PageLayout } from '@components/layouts/PageLayout'
import { RichTextEditorField } from '@components/molecules/RichTextEditorField'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('help.form')
const tHelp = scopedTranslation('help')
const tButtons = scopedTranslation('shared.buttons')

export type FaqFormProps = {
  faq: { id: number | null; title: string; content: string }
}

export default function FaqForm({ faq }: FaqFormProps) {
  const form = useForm({ title: faq.title, content: faq.content })
  const errors: FieldErrors = Object.fromEntries(
    Object.entries(form.errors).map(([field, message]) => [field, { type: 'server', message }])
  )
  const backHref = faq.id ? `/help?faq=${faq.id}` : '/help'

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.transform((data) => ({ faq: data }))
    if (faq.id) form.patch(`/faqs/${faq.id}`)
    else form.post('/faqs')
  }

  return (
    <PageLayout.Root>
      <PageLayout.Header
        icon="help-circle"
        heading={faq.id ? t('edit_title') : tHelp('new_faq')}
        subtitle={t('description')}
      />
      <PageLayout.Body>
        <form onSubmit={submit} className="tw:flex tw:max-w-3xl tw:flex-col tw:gap-6 tw:pt-4">
          <TextInput
            name="title"
            label={t('title_label')}
            required
            errors={errors}
            value={form.data.title}
            onChange={(event) => form.setData('title', event.target.value)}
          />
          <RichTextEditorField
            name="content"
            label={t('content_label')}
            required
            errors={errors}
            minHeight={480}
            maxHeight={1200}
            value={form.data.content}
            onChange={(value) => form.setData('content', value)}
          />
          <div className="tw:flex tw:justify-end tw:gap-2">
            <Link as="inertiaLink" href={backHref} rank="secondary">
              {tButtons('cancel')}
            </Link>
            <Button type="submit" disabled={form.processing}>
              {t('save')}
            </Button>
          </div>
        </form>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
